import Button from '@atoms/Button/Button';
import Drawer from '@atoms/Drawer/Drawer';
import Icon from '@atoms/Icon/Icon';
import ShadowContainerVertical from '@atoms/ShadowContainerVertical/ShadowContainerVertical';
import { IContentFilters } from '@models/IContentFilters';
import { IStoredFilters } from '@models/IStoredFilters';
import ContentFilterTags from '@molecules/ContentFilterTags/ContentFilterTags';
import FilterGroup from '@molecules/FilterGroup/FilterGroup';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { PropsWithChildren, useEffect } from 'react';

interface IFormWrapper {
  readonly values: IStoredFilters;
  readonly onChange: (params: IStoredFilters) => void;
}

const FormWrapper = ({
  values,
  children,
  onChange,
}: PropsWithChildren<IFormWrapper>) => {
  useEffect(() => {
    if (values) onChange?.(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return (
    <Form
      key="Form"
      className="relative flex h-full flex-col px-5 text-black-100 md:px-10 lg:px-20"
    >
      {children}
    </Form>
  );
};

const ContentFilters = ({
  applyFilters,
  appliedFilters,
  clearFacets,
  filters,
  filtersApplyLabel,
  filtersApplySingleLabel,
  filtersCloseLabel,
  filtersClearLabel,
  filtersTitleLabel,
  isDrawerOpen,
  fetchTotal,
  setIsDrawerOpen,
  showFacets,
  totalFacetsToApply,
  filtersToggleLabel,
  loading = false,
  classes,
  areThereFiltersAvailable = false,
  error = false,
  showExpandedFilters = true,
}: IContentFilters) => {
  const { clearFiltersAriaLabel } = useAriaLabels();
  const populatedFilters = filters?.filter((filter) => filter.values?.length);

  return (
    <>
      <button
        disabled={loading || !!error || !areThereFiltersAvailable}
        onClick={() => setIsDrawerOpen?.((oldState) => !oldState)}
        type="button"
        className={classNames(
          classes,
          loading || error || !areThereFiltersAvailable
            ? 'text-grey-500'
            : 'text-black-100 dark:text-white',
          'block'
        )}
        data-testid="Content-Filters-Toggle"
      >
        <p
          className="body-two flex items-center justify-center"
          data-testid="Content-Filters-Toggle-Label"
        >
          {filtersToggleLabel}
          <Icon
            name="arrow_right"
            colorClassName="dark:text-yellow"
            className="ml-1"
          />
        </p>
      </button>
      <Drawer setIsOpen={setIsDrawerOpen} isOpen={isDrawerOpen}>
        <Formik initialValues={appliedFilters} onSubmit={applyFilters}>
          {({ values, setFieldValue }) => (
            <FormWrapper onChange={() => fetchTotal(values)} values={values}>
              <div className="mb-7.5 flex flex-row justify-between lg:mb-15">
                <div className="relative flex flex-col items-center justify-center">
                  <p
                    className="body-one font-normal"
                    data-testid="Content-Filters-Title"
                  >
                    {filtersTitleLabel}
                  </p>

                  <div className="mt-[14px] h-[6px] w-[6px] rounded-full bg-yellow" />
                </div>
                <button
                  type="button"
                  aria-label={clearFiltersAriaLabel}
                  onClick={clearFacets}
                  data-testid="Content-Filters-Clear"
                >
                  <div className="relative flex flex-col items-center justify-center">
                    <p
                      className="body-one font-light text-grey-600"
                      data-testid="Content-Filters-Clear-Label"
                    >
                      {filtersClearLabel}
                    </p>

                    <div className="mt-[14px] h-[6px] w-[6px] rounded-full bg-grey-100" />
                  </div>
                </button>
              </div>
              <div className="dark mb-7.5 flex flex-wrap gap-2 lg:mb-13">
                <ContentFilterTags
                  values={values}
                  setFieldValue={setFieldValue}
                  filters={filters}
                />
              </div>
              <ShadowContainerVertical
                shadowColour="from-grey-100"
                className="flex h-full grow flex-col overflow-y-hidden scrollbar-hide"
                shadowHeight="h-20"
              >
                {showFacets &&
                  populatedFilters.map((filter) => {
                    const isGroupOpenByDefault =
                      filter.values?.filter((item) => item.isActive).length >
                        0 || showExpandedFilters;

                    return (
                      <FilterGroup
                        key={`${filter.key}-facet-group`}
                        groupKey={filter.key}
                        values={filter.values}
                        label={filter.label}
                        openByDefault={isGroupOpenByDefault}
                        helpText={filter.helpText}
                        filtersCloseLabel={filtersCloseLabel}
                        showExpandButton={populatedFilters.length > 1}
                      />
                    );
                  })}
              </ShadowContainerVertical>
              <div className="bottom-7.5 flex w-full justify-center py-7.5 lg:absolute lg:bottom-20 lg:right-20 lg:w-auto lg:py-0">
                <Button
                  key="sumbit-content-filters"
                  type="submit"
                  disabled={totalFacetsToApply === 0}
                  dataTestId="Content-Filters-Submit"
                >
                  {totalFacetsToApply === 1
                    ? filtersApplySingleLabel?.replace(
                        '{0}',
                        `${totalFacetsToApply}`
                      )
                    : filtersApplyLabel?.replace(
                        '{0}',
                        `${totalFacetsToApply}`
                      )}
                </Button>
              </div>
            </FormWrapper>
          )}
        </Formik>
      </Drawer>
    </>
  );
};

export default ContentFilters;
